import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { Edit2, Save, X, Loader2 } from 'lucide-react';
import { Alert, AlertDescription } from '../ui/alert';

interface Format {
  id: string;
  name: string;
  canonical_name: string;
  count: number;
}

interface Operator {
  id: string;
  name: string;
}

interface InlineFormatEditorProps {
  format: Format;
  storeId: string;
  storeName: string;
  operators: Operator[];
  onSave: (storeId: string, formatId: string, operatorId: string) => Promise<void>;
  showIds?: boolean;
}

export const InlineFormatEditor: React.FC<InlineFormatEditorProps> = ({
  format,
  storeId,
  storeName,
  operators,
  onSave,
  showIds = false
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [selectedOperatorId, setSelectedOperatorId] = useState<string>('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleEdit = () => {
    setIsEditing(true);
    setError(null);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setSelectedOperatorId('');
    setError(null);
  };

  const handleSave = async () => {
    if (!selectedOperatorId) {
      setError('Please select an operator');
      return;
    }

    try {
      setIsSaving(true);
      setError(null);
      await onSave(storeId, format.id, selectedOperatorId);
      setIsEditing(false);
      setSelectedOperatorId('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save assignment');
    } finally {
      setIsSaving(false);
    }
  };

  if (isEditing) {
    return (
      <div className="flex items-center gap-2 p-2 border rounded-lg bg-muted/50">
        <div className="flex-1">
          <div className="text-sm font-medium">
            {format.name} ×{format.count}
            {showIds && (
              <Badge variant="outline" className="ml-2 text-xs font-mono">
                {format.id}
              </Badge>
            )}
          </div>
          <div className="text-xs text-muted-foreground">
            Store: {storeName}
          </div>
        </div>
        
        <Select value={selectedOperatorId} onValueChange={setSelectedOperatorId}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Select operator..." />
          </SelectTrigger>
          <SelectContent>
            {operators.map((operator) => (
              <SelectItem key={operator.id} value={operator.id}>
                {operator.name}
                {showIds && (
                  <span className="ml-2 text-xs font-mono text-muted-foreground">
                    {operator.id}
                  </span>
                )}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="flex gap-1">
          <Button
            size="sm"
            onClick={handleSave}
            disabled={isSaving || !selectedOperatorId}
          >
            {isSaving ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <Save className="h-3 w-3" />
            )}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={handleCancel}
            disabled={isSaving}
          >
            <X className="h-3 w-3" />
          </Button>
        </div>

        {error && (
          <Alert variant="destructive" className="mt-2">
            <AlertDescription className="text-xs">{error}</AlertDescription>
          </Alert>
        )}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <Badge variant="outline" className="text-xs">
        {format.name} ×{format.count}
        {showIds && (
          <span className="ml-1 font-mono">
            · {format.id}
          </span>
        )}
      </Badge>
      <Button
        size="sm"
        variant="ghost"
        onClick={handleEdit}
        className="h-6 w-6 p-0"
        title="Edit operator assignment"
      >
        <Edit2 className="h-3 w-3" />
      </Button>
    </div>
  );
};
